import { ControlClientError } from "@kontextwerk/control-web-client"
import {
  rejectControlContext,
  runWithConfirmedReauthentication,
} from "@kontextwerk/control-web-frame/shared/logic/authentication"
import {
  clearImportantActionReauth,
  hasRecentImportantActionReauth,
  requestImportantActionReauth,
  type ImportantActionReauthConfig,
} from "@kontextwerk/web-sdk/ui/security/importantActionReauth"

const reauthenticationPresentation = {
  clear: clearImportantActionReauth,
  hasRecent: hasRecentImportantActionReauth,
  request: requestImportantActionReauth,
}

/**
 * Typed result of one confirmed identity change. `conflict` covers stale revisions and server
 * preconditions (for example the last active owner); `obsolete` a changed or ended context.
 */
export type ConfirmedChangeOutcome<T> =
  | Readonly<{ status: "done"; value: T }>
  | Readonly<{ status: "cancelled" }>
  | Readonly<{ status: "conflict" }>
  | Readonly<{ status: "forbidden" }>
  | Readonly<{ status: "obsolete" }>
  | Readonly<{ status: "failed" }>

const reasonOf = (error: ControlClientError): string =>
  typeof error.detail === "string" ? error.detail : (error.reasonCode ?? "")

/**
 * Identity changes always ask for the current password right before their single write. The
 * captured context must still be current afterwards; a rejected write is never replayed.
 */
export const runConfirmedChange = async <T>(
  config: ImportantActionReauthConfig,
  action: () => Promise<T>,
): Promise<ConfirmedChangeOutcome<T>> => {
  try {
    const value = await runWithConfirmedReauthentication(
      config,
      action,
      reauthenticationPresentation,
    )

    return value === undefined ? { status: "cancelled" } : { status: "done", value }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return { status: "obsolete" }

    if (!(error instanceof ControlClientError)) return { status: "failed" }

    const status = error.statusCodeOrStatus

    // The KB transport already dropped the protected state for an ended session.
    if (status === 401) return { status: "obsolete" }

    if (status === 409 && reasonOf(error) === "context_changed") {
      rejectControlContext()

      return { status: "obsolete" }
    }

    if (status === 409) return { status: "conflict" }

    if (status === 403) return { status: "forbidden" }

    return { status: "failed" }
  }
}
